import VehicleOffering from '@carvana/merch-contracts/VehicleOffering';
import z from 'zod';
import PickupAndDeliveryOption, {
  PickupAndDeliveryOptionUpperCase
} from '~/constants/common/PickupAndDeliveryOption';

const AppliedBooleanFiltersSchema = z.object({
  isFreeDelivery: z.boolean()
});

export type AppliedBooleanFilters = z.infer<typeof AppliedBooleanFiltersSchema>;

const ModelTypeSchema = z.object({
  name: z.string(),
  trims: z.array(z.string()).optional()
});

export type ModelType = z.infer<typeof ModelTypeSchema>;

const MakeTypeSchema = z.object({
  name: z.string(),
  models: z.array(ModelTypeSchema).optional(),
  parentModels: z.array(ModelTypeSchema).optional()
});

export type MakeType = z.infer<typeof MakeTypeSchema>;

const AppliedListFiltersSchema = z.object({
  bodyStyles: z.array(z.string()),
  categoryIds: z.array(z.string()),
  colors: z.array(z.string()),
  cvnaFeatureIds: z.array(z.string()),
  cvnaFeatures: z.array(z.string()),
  cylinderCounts: z.array(z.string()),
  driveTypes: z.array(z.string()),
  features: z.array(z.string()),
  fuelTypes: z.array(z.string()),
  interiorColors: z.array(z.string()),
  luxuryTypes: z.array(z.string()),
  offerings: z.array(z.nativeEnum(VehicleOffering)),
  makes: z.array(MakeTypeSchema),
  seatMaterials: z.array(z.string()),
  sizeClasses: z.array(z.string()),
  sportTypes: z.array(z.string()),
  tagIds: z.array(z.string()),
  transmissions: z.array(z.string()),
  trims: z.array(z.string()),
  truckBedLengths: z.array(z.string()),
  truckCabTypes: z.array(z.string())
});

export type AppliedListFilters = z.infer<typeof AppliedListFiltersSchema>;

const RangeFacetSchema = z.union([
  z.object({ max: z.number(), min: z.number().optional() }),
  z.object({ max: z.number().optional(), min: z.number() })
]);

export type RangeFacet = z.infer<typeof RangeFacetSchema>;

const FinanceTypeSchema = z.union([
  z.object({
    downPayment: z.number(),
    monthlyPayment: z.number().optional()
  }),
  z.object({
    downPayment: z.number().optional(),
    monthlyPayment: z.number()
  })
]);

export type FinanceType = z.infer<typeof FinanceTypeSchema>;

const FinanceFilterSchema = z.object({
  finance: FinanceTypeSchema
});

export type FinanceFilter = z.infer<typeof FinanceFilterSchema>;

const PickupAndDeliveryOptionSchema = z.nativeEnum(PickupAndDeliveryOption);
const PickupAndDeliveryOptionUpperCaseSchema = z.nativeEnum(
  PickupAndDeliveryOptionUpperCase
);

const AvailabilityTypeSchema = z.object({
  pickupAndDeliveryOption: PickupAndDeliveryOptionSchema.or(
    PickupAndDeliveryOptionUpperCaseSchema
  ),
  days: z.number()
});

export type AvailabilityType = z.infer<typeof AvailabilityTypeSchema>;

const AvailabilityFilterSchema = z.object({
  earliestDayToDeliveryOrPickup: AvailabilityTypeSchema
});

export type AvailabilityFilter = z.infer<typeof AvailabilityFilterSchema>;

const AppliedRangeFiltersSchema = z.object({
  cargoVolumeFolded: RangeFacetSchema,
  cargoVolumeMax: RangeFacetSchema,
  evRange: RangeFacetSchema,
  horsePower: RangeFacetSchema,
  mileage: RangeFacetSchema,
  mpg: RangeFacetSchema,
  overallLength: RangeFacetSchema,
  price: RangeFacetSchema,
  remainingBatteryWarrantyMiles: RangeFacetSchema,
  remainingBatteryWarrantyMonths: RangeFacetSchema,
  remainingDriveTrainWarrantyMiles: RangeFacetSchema,
  remainingDriveTrainWarrantyMonths: RangeFacetSchema,
  remainingWarrantyMiles: RangeFacetSchema,
  remainingWarrantyMonths: RangeFacetSchema,
  seatingCapacity: RangeFacetSchema,
  torque: RangeFacetSchema,
  towingCapacity: RangeFacetSchema,
  transportCost: RangeFacetSchema,
  truckBedVolume: RangeFacetSchema,
  trunkCapacity: RangeFacetSchema,
  year: RangeFacetSchema
});

export type AppliedRangeFilters = z.infer<typeof AppliedRangeFiltersSchema>;

const VehicleIdsSchema = z.object({
  vehicleIds: z.array(z.number())
});

export type VehicleIdsFilter = z.infer<typeof VehicleIdsSchema>;

const AppliedFiltersSchema = AppliedBooleanFiltersSchema.partial()
  .and(AppliedListFiltersSchema.partial())
  .and(AppliedRangeFiltersSchema.partial())
  .and(FinanceFilterSchema.partial())
  .and(AvailabilityFilterSchema.partial())
  .and(VehicleIdsSchema.partial());

export type AppliedFilters = z.infer<typeof AppliedFiltersSchema>;

export default AppliedFiltersSchema;
